import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useRepoParams } from '../../../hooks/useRepoParams';
import { repoApi, pullRequestApi } from '../../../api/client';
import { DiffView } from '../../../components/DiffView';
import './NewPullRequestPage.css';

interface Branch {
    name: string;
    sha: string;
}

interface CompareResult {
    mergeBase: string | null;
    commits: any[];
    files: any[];
    canMerge: boolean;
}

export function NewPullRequestPage() {
    const { owner, repo } = useRepoParams();
    const navigate = useNavigate();
    const repoPath = `${owner}/${repo}`;

    const [branches, setBranches] = useState<Branch[]>([]);
    const [sourceBranch, setSourceBranch] = useState('');
    const [targetBranch, setTargetBranch] = useState('main'); // Default to main/master?

    const [comparison, setComparison] = useState<CompareResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [compareLoading, setCompareLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [isDraft, setIsDraft] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Initial load
    useEffect(() => {
        loadBranches();
    }, [repoPath]);

    // Compare when branches change
    useEffect(() => {
        if (sourceBranch && targetBranch && sourceBranch !== targetBranch) {
            compareBranches();
        } else {
            setComparison(null);
        }
    }, [sourceBranch, targetBranch]);

    const loadBranches = async () => {
        try {
            setLoading(true);
            // Verify repo exists first? Or just lets API handle it
            const branchesData = await repoApi.getBranches(repoPath);
            console.log('Loaded branches:', branchesData); // Debug log

            // Filter out branches without valid names
            const validBranches = (branchesData || []).filter(b => b && b.name);
            setBranches(validBranches);

            // Set default branches
            if (validBranches.length > 0) {
                // Find main or master for target
                const mainBranch = validBranches.find(b => b.name === 'main' || b.name === 'master');
                const target = mainBranch?.name || validBranches[0].name;
                setTargetBranch(target);

                // For source, default to empty or first non-target
                // Usually user comes from "Create Merge Request" on a pushed branch
                // We might support query params later ?source=...
                const params = new URLSearchParams(window.location.search);
                const sourceParam = params.get('source');
                if (sourceParam) {
                    setSourceBranch(sourceParam);
                }
            }
        } catch (err: any) {
            console.error('Failed to load branches:', err); // Debug log
            setError(err.message || 'Failed to load branches');
        } finally {
            setLoading(false);
        }
    };

    const compareBranches = async () => {
        try {
            setCompareLoading(true);
            setError(null);

            // Auto fill title with last commit message if empty
            // This is a nice-to-have, maybe later

            const result = await pullRequestApi.compare(repoPath, sourceBranch, targetBranch);
            setComparison(result);

            // If title is empty and we have commits, use the last commit message
            if (!title && result.commits && result.commits.length > 0) {
                const lastCommit = result.commits[0]; // Logic depends on order
                // Usually client.ts returns commits in some order. Assuming check api logic.
                // The API returns commits from mergeBase to source. Newest last usually?
                // Or git log order (newest first). Let's assume newest first.
                setTitle(lastCommit.message.split('\n')[0]);
            }
        } catch (err: any) {
            setComparison(null);
            setError(err.message || 'Failed to compare branches');
        } finally {
            setCompareLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !sourceBranch || !targetBranch) return;

        try {
            setSubmitting(true);
            const pr = await pullRequestApi.create(repoPath, {
                title,
                description,
                sourceBranch,
                targetBranch,
                isDraft
            });
            // Redirect to PR details
            navigate(`/${repoPath}/-/pulls/${pr.id}`);
        } catch (err: any) {
            setError(err.message || 'Failed to create pull request');
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="loading-state">Loading...</div>;
    }

    return (
        <div className="new-pr-page">
            <div className="page-header">
                <h1>Create New Merge Request</h1>
                <Link to={`/${repoPath}/-/pulls`} className="btn-link">Back to list</Link>
            </div>

            {error && <div className="error-state">{error}</div>}

            <div className="branch-selector-card">
                <div className="selector-row">
                    <div className="selector-group">
                        <label>Source Branch</label>
                        <select
                            className="branch-select"
                            value={sourceBranch}
                            onChange={(e) => setSourceBranch(e.target.value)}
                        >
                            <option value="">Select branch...</option>
                            {branches.map(b => (
                                <option key={b.name} value={b.name}>{b.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="compare-arrow">→</div>

                    <div className="selector-group">
                        <label>Target Branch</label>
                        <select
                            className="branch-select"
                            value={targetBranch}
                            onChange={(e) => setTargetBranch(e.target.value)}
                        >
                            {branches.map(b => (
                                <option key={b.name} value={b.name}>{b.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {compareLoading && <div className="loading-state">Comparing branches...</div>}

            {comparison && (
                <>
                    <form className="pr-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                className="form-input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Merge request title"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Description (Markdown supported)</label>
                            <textarea
                                className="form-textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Describe your changes..."
                            />
                        </div>

                        <div className="form-group">
                            <label className="draft-option">
                                <input
                                    type="checkbox"
                                    checked={isDraft}
                                    onChange={(e) => setIsDraft(e.target.checked)}
                                />
                                Mark as draft
                            </label>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                {submitting ? 'Creating...' : 'Create Merge Request'}
                            </button>
                        </div>
                    </form>

                    <div className="comparison-result">
                        <h2>Commits ({comparison.commits.length})</h2>
                        <div className="commits-list">
                            {comparison.commits.map((commit: any) => (
                                <div key={commit.sha} className="commit-item">
                                    <div className="commit-message">{commit.message.split('\n')[0]}</div>
                                    <div className="commit-meta">
                                        {commit.author.name} committed on {new Date(commit.author.timestamp * 1000).toLocaleDateString()}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <h2>File Changes</h2>
                        <DiffView files={comparison.files as any} stats={{ additions: 0, deletions: 0, changed: comparison.files.length }} />
                    </div>
                </>
            )}

            {!compareLoading && !comparison && sourceBranch && targetBranch && (
                <div className="empty-state">
                    No changes to merge or branches are identical.
                </div>
            )}
        </div>
    );
}
